<template>
  <div
    class="min-h-screen flex flex-col lg:flex-row justify-center lg:gap-x-16"
  >
    <div
      class="flex-1 flex-col justify-center items-center bg-primary-dark m-4 rounded-2xl hidden lg:flex relative"
    >
      <div class="flex flex-col gap-y-6 items-center">
        <img
          src="../assets/chat-polls-logo.png"
          alt="logo"
          class="object-contain h-24"
        />
        <img
          src="../assets/background-circle.png"
          alt="logo"
          class="object-contain h-96 absolute bottom-0 left-4"
        />
        <p
          class="text-white text-center font-poppins text-xl leading-9 font-semibold max-w-md"
        >
          Your quick and easy tool for gathering opinions and making informed
          decisions within Google Chat. Log in and continue collaborating
          effortlessly.
        </p>
      </div>
    </div>
    <div
      class="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-0 py-6 lg:py-0"
    >
      <h1
        class="text-primary-dark-text font-poppins text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-4 lg:mb-0"
      >
        Sign In to Chat-Polls
      </h1>
      <form
        @submit.prevent="handleSubmit"
        class="mt-4 lg:mt-6 flex flex-col gap-y-3 lg:gap-y-4 max-w-xl"
      >
        <div v-if="errorMessage" class="text-red-600 font-onest">
          {{ errorMessage }}
        </div>

        <FormInput
          v-model="email"
          label="Email"
          type="email"
          placeholder="info@chat-polls.com"
          id="email"
        />

        <FormInput
          v-model="password"
          label="Password"
          type="password"
          placeholder="password"
          id="password"
        />

        <div class="flex items-center">
          <input
            id="remember-me"
            type="checkbox"
            v-model="rememberMe"
            class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label
            for="remember-me"
            class="ml-2 block text-gray-600 text-sm lg:text-base"
          >
            Keep me logged in for up to 30 days
          </label>
        </div>

        <div class="flex flex-col space-y-2 lg:space-y-3 mt-1">
          <a
            href="#"
            class="text-primary hover:text-primary-dark font-medium underline underline-offset-8 text-sm lg:text-base"
          >
            Forgot Your Password?
          </a>
          <a
            href="#"
            class="text-primary hover:text-primary-dark font-medium underline underline-offset-8 text-sm lg:text-base"
          >
            Forgot Your Email Address?
          </a>
        </div>

        <div class="my-4 lg:my-6">
          <button
            type="submit"
            :disabled="loading"
            class="bg-btn-gradient text-white py-3 px-8 lg:px-12 rounded-md font-onest font-medium text-base lg:text-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 w-full sm:w-auto"
          >
            {{ loading ? "Signing In..." : "Sign In" }}
          </button>
        </div>
      </form>

      <!-- Divider -->
      <div class="relative my-4 lg:my-6 max-w-xl">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-gray-300"></div>
        </div>
        <div class="relative flex justify-center text-sm">
          <span class="px-2 bg-white text-gray-500 font-onest">or</span>
        </div>
      </div>

      <!-- Social Login Buttons -->
      <div class="flex flex-col md:flex-row gap-4 max-w-xl">
        <button
          type="button"
          class="flex-1 bg-btn-gradient rounded-md p-0.5 group"
        >
          <div
            class="flex items-center justify-center gap-2 lg:gap-3 px-4 lg:px-6 py-3 bg-white rounded text-primary-dark-text font-onest font-medium text-sm lg:text-lg group-hover:bg-gray-50 group-focus:outline-none group-focus:ring-2 group-focus:ring-primary group-focus:ring-offset-2 transition-all duration-200 h-full"
          >
            <GoogleIcon />
            <span class="hidden sm:inline">Sign In With Google</span>
            <span class="sm:hidden">Google</span>
          </div>
        </button>

        <button
          type="button"
          class="flex-1 bg-btn-gradient rounded-md p-0.5 group"
        >
          <div
            class="flex items-center justify-center gap-2 lg:gap-3 px-4 lg:px-6 py-3 bg-white rounded text-primary-dark-text font-onest font-medium text-sm lg:text-lg group-hover:bg-gray-50 group-focus:outline-none group-focus:ring-2 group-focus:ring-primary group-focus:ring-offset-2 transition-all duration-200 h-full"
          >
            <AppleIcon />
            <span class="hidden sm:inline">Sign In With Apple</span>
            <span class="sm:hidden">Apple</span>
          </div>
        </button>
      </div>

      <!-- Create Account Link -->
      <div class="mt-4 lg:mt-6 max-w-xl">
        <span class="text-gray-600 font-onest text-sm lg:text-base">
          Not on Chat-Polls?
        </span>
        <router-link
          to="/register"
          class="text-primary hover:text-primary-dark font-medium underline underline-offset-4 text-sm lg:text-base"
        >
          Create an account
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { login } from "../services/auth";
import FormInput from "../components/ui/FormInput.vue";
import GoogleIcon from "../components/ui/GoogleIcon.vue";
import AppleIcon from "../components/ui/AppleIcon.vue";

export default {
  components: {
    FormInput,
    GoogleIcon,
    AppleIcon,
  },
  data() {
    return {
      email: "",
      password: "",
      rememberMe: false,
      errorMessage: "",
      loading: false,
    };
  },
  methods: {
    async handleSubmit() {
      this.errorMessage = "";
      this.loading = true;
      if (!this.validateEmail(this.email) || !this.password.trim()) {
        this.errorMessage = "Please enter valid email and password.";
        this.loading = false;
        return;
      }
      try {
        await login(this.email, this.password);
        this.$router.push("/dashboard");
      } catch (error) {
        if (error.response && error.response.status) {
          const statusCode = error.response.status;
          if (statusCode === 404) {
            this.errorMessage = "Email does not exist.";
          } else if (statusCode === 401) {
            this.errorMessage = "Wrong email or password.";
          } else {
            this.errorMessage = "Invalid login credentials.";
          }
        } else {
          this.errorMessage = "An unexpected error occurred.";
        }
      } finally {
        this.loading = false;
      }
    },
    clearError() {
      this.errorMessage = "";
    },
    validateEmail(email) {
      if (!email) return false;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email.trim());
    },
  },
};
</script>
